import { useEffect, useState } from 'react';

export default function TrackRecord() {
  const [animatedValues, setAnimatedValues] = useState({
    revenue1: 0,
    revenue2: 0,
    revenue3: 0,
    countries: 0
  });

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      setAnimatedValues({
        revenue1: Math.floor(230 * progress),
        revenue2: Math.floor(550 * progress),
        revenue3: Math.floor(5 * progress * 10) / 10,
        countries: Math.floor(13 * progress)
      });

      if (step >= steps) {
        clearInterval(timer);
        setAnimatedValues({
          revenue1: 230,
          revenue2: 550,
          revenue3: 5,
          countries: 13
        });
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  return (
    <section className="scroll-animate py-36 bg-gradient-to-b from-white to-blue-50/50 relative overflow-hidden">
      {/* Subtle gradient overlay */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-blue-100/30 to-transparent"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-20">
        {/* Title */}
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-slate-900 mb-4">
            Proven Results
          </h2>
          <p className="text-lg text-slate-600">
            Real metrics from our journey building global platforms
          </p>
        </div>

        {/* 4 Metric Cards with glass-morphism */}
        <div className="grid grid-cols-4 gap-6 mb-16">
          {/* Card 1: Revenue 1 */}
          <div className="relative bg-white/70 backdrop-blur-lg rounded-2xl p-8 shadow-xl border border-white/50 hover:scale-105 transition-transform duration-300 cursor-pointer">
            <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/10 rounded-full -mr-10 -mt-10"></div>
            <div className="relative z-10">
              <div className="text-4xl font-bold text-slate-900 mb-2">
                ₩{animatedValues.revenue1}M
              </div>
              <div className="text-sm font-medium text-slate-600 mb-4">
                Initial Funding
              </div>
              {/* Mini chart line */}
              <div className="flex items-end gap-1 h-12">
                {[30, 45, 40, 60, 55, 75, 70, 90, 100].map((height, i) => (
                  <div 
                    key={i} 
                    className="flex-1 bg-gradient-to-t from-blue-500 to-blue-300 rounded-t transition-all duration-300"
                    style={{ height: `${height}%` }}
                  ></div>
                ))}
              </div>
            </div>
          </div>

          {/* Card 2: Revenue 2 with circular progress */}
          <div className="relative bg-white/70 backdrop-blur-lg rounded-2xl p-8 shadow-xl border border-white/50 hover:scale-105 transition-transform duration-300 cursor-pointer">
            <div className="absolute bottom-0 left-0 w-20 h-20 bg-teal-500/10 rounded-full -ml-10 -mb-10"></div>
            <div className="relative z-10">
              <div className="text-4xl font-bold text-slate-900 mb-2">
                ₩{animatedValues.revenue2}M
              </div>
              <div className="text-sm font-medium text-slate-600 mb-4">
                Current Valuation
              </div>
              {/* Circular progress ring */}
              <div className="flex items-center gap-3">
                <div className="relative w-16 h-16">
                  <svg className="transform -rotate-90 w-16 h-16">
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      stroke="#e2e8f0"
                      strokeWidth="6"
                      fill="none"
                    />
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      stroke="#14b8a6"
                      strokeWidth="6"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 28}`}
                      strokeDashoffset={`${2 * Math.PI * 28 * (1 - 2.39)}`}
                      className="transition-all duration-1000"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-teal-600">
                    239%
                  </div>
                </div>
                <div className="text-xs text-slate-600">Growth Rate</div>
              </div>
            </div>
          </div>

          {/* Card 3: Revenue 3 with rocket */}
          <div className="relative bg-white/70 backdrop-blur-lg rounded-2xl p-8 shadow-xl border border-white/50 hover:scale-105 transition-transform duration-300 cursor-pointer">
            <div className="absolute top-1/2 right-0 w-20 h-20 bg-purple-500/10 rounded-full -mr-10"></div>
            <div className="relative z-10">
              <div className="text-4xl font-bold text-slate-900 mb-2">
                ₩{animatedValues.revenue3}B
              </div>
              <div className="text-sm font-medium text-slate-600 mb-4">
                Target Revenue
              </div>
              {/* Rocket icon launching */}
              <div className="flex items-center gap-2">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full animate-bounce">
                  <i className="ri-rocket-line text-2xl text-white"></i>
                </div>
                <div className="text-xs text-slate-600">2026 Goal</div>
              </div>
            </div>
          </div>

          {/* Card 4: Countries with globe */}
          <div className="relative bg-white/70 backdrop-blur-lg rounded-2xl p-8 shadow-xl border border-white/50 hover:scale-105 transition-transform duration-300 cursor-pointer">
            <div className="absolute top-0 left-0 w-20 h-20 bg-blue-500/10 rounded-full -ml-10 -mt-10"></div>
            <div className="relative z-10">
              <div className="text-4xl font-bold text-slate-900 mb-2">
                {animatedValues.countries}
              </div>
              <div className="text-sm font-medium text-slate-600 mb-4">
                Countries Served
              </div>
              {/* Animated globe with pins */}
              <div className="relative w-16 h-16 mx-auto">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-teal-500 rounded-full opacity-20 animate-ping"></div>
                <div className="relative w-16 h-16 flex items-center justify-center bg-gradient-to-br from-blue-500 to-teal-500 rounded-full">
                  <i className="ri-global-line text-2xl text-white"></i>
                </div>
                {/* Country pins */}
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                <div className="absolute top-2 -left-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                <div className="absolute -bottom-1 right-2 w-3 h-3 bg-yellow-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom: 2 Certification badges */}
        <div className="flex justify-center gap-12">
          {/* Badge 1 */}
          <div className="flex items-center gap-4 bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer">
            <div className="relative w-20 h-20">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full opacity-20"></div>
              <div className="relative w-20 h-20 flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 rounded-full">
                <i className="ri-award-line text-3xl text-white"></i>
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
                <i className="ri-check-line text-xs text-white"></i>
              </div>
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">Venture Certified</div>
              <div className="text-sm text-slate-600">Official Recognition 2024</div>
            </div>
          </div>

          {/* Badge 2 */}
          <div className="flex items-center gap-4 bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer">
            <div className="relative w-20 h-20">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-blue-600 rounded-full opacity-20"></div>
              <div className="relative w-20 h-20 flex items-center justify-center bg-gradient-to-br from-teal-500 to-blue-600 rounded-full">
                <i className="ri-shield-star-line text-3xl text-white"></i>
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center border-2 border-white">
                <i className="ri-star-fill text-xs text-white"></i>
              </div>
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">Innovation Award</div>
              <div className="text-sm text-slate-600">Tech Excellence 2024</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
